/**
 * Helper functions for add to cart success flow
 * Tách logic phức tạp từ useAddToCart để code dễ đọc và maintain hơn
 */

import { toast } from '@/components/common/toast'
import { ToastAddToCartData } from '@/components/common/toast/toast-cart'
import { prefetchImage } from '@/lib/utils/image-prefetch'
import { processImageUrl } from '@/lib/utils/image-url'
import type { CampaignInfo } from '@/providers/campaign-provider'
import { __cCartService } from '@/services/client/__c.cart.service'
import type { CartMiniItem } from '@/types/cart'
import type { QueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from '../../constants'

/**
 * Hiển thị toast thêm vào giỏ hàng thành công
 * Prefetch image trước để toast hiển thị mượt mà hơn
 */
export async function showAddToCartSuccessToast(
  toastOptions: ToastAddToCartData
) {
  try {
    // Prefetch image để toast hiển thị mượt mà
    await prefetchImage(processImageUrl(toastOptions.imageUrl, 120))
  } catch {
    // Ignore prefetch errors
  }

  // Delay nhỏ để đảm bảo UI đã render xong
  setTimeout(() => {
    toast.addToCartSuccess(toastOptions)
  }, 30)
}

/**
 * Kiểm tra và hiển thị campaign reminder toast
 * Logic giống với cart-block-header để đảm bảo consistency
 */
export async function checkAndShowCampaignReminder({
  cartMini,
  refetchCartMini,
  campaignData,
  isCampaignActive,
  queryClient,
}: {
  cartMini: { items?: CartMiniItem[] } | undefined
  refetchCartMini: () => Promise<{ data?: { items?: CartMiniItem[] } }>
  campaignData: CampaignInfo | undefined
  isCampaignActive: boolean
  queryClient: QueryClient
}): Promise<
  Awaited<ReturnType<typeof __cCartService.getCartMini>> | undefined
> {
  try {
    if (!isCampaignActive) {
      return
    }
    const campaignPercent = Number((campaignData?.data?.percent as number) ?? 0)
    const blockSize = Number((campaignData?.data?.max_product as number) ?? 0)
    const excludeCollectionIds =
      (campaignData?.data?.exclude_collection_ids as string[]) ?? []

    // Refetch cart mini để lấy data mới nhất
    const { data: refreshedMini } = await refetchCartMini()
    const prevItems = cartMini?.items ?? []
    const items = refreshedMini?.items ?? prevItems

    // Lấy full cart mini data để return cho checkAndShowGiftEligibilityToast
    // refetchCartMini chỉ return { data: { items } }, nên cần fetch full data
    // Nhưng vì refetchCartMini đã fetch rồi, nên data có thể đã có trong cache
    // Thử lấy từ cache trước, nếu không có mới fetch
    let fullCartMiniData = queryClient.getQueryData([QUERY_KEYS.CART_MINI]) as
      | Awaited<ReturnType<typeof __cCartService.getCartMini>>
      | undefined

    if (!fullCartMiniData) {
      fullCartMiniData = await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.CART_MINI],
        queryFn: () => __cCartService.getCartMini(),
        staleTime: 0,
      })
    }

    // Xác định item vừa thêm và kiểm tra có phải excluded collection không
    const addedItemIsExcluded = checkIfAddedItemIsExcluded({
      prevItems,
      items,
      excludeCollectionIds,
    })

    // Đếm số item đã được áp dụng khuyến mãi (giống logic cart-block-header)
    const totalPromotion = items.filter(
      (it) => Number(it.off_percent ?? 0) === campaignPercent
    ).length
    const promotionRemaining = Math.max(blockSize - totalPromotion, 0)

    // Hiển thị campaign reminder nếu đủ điều kiện
    if (
      isCampaignActive &&
      campaignPercent > 0 &&
      blockSize > 0 &&
      promotionRemaining > 0 &&
      addedItemIsExcluded === false
    ) {
      setTimeout(() => {
        toast.campaignReminder({
          discount: campaignPercent,
          promotionRemaining,
        })
      }, 100)
    }

    // Return cart mini data để reuse trong checkAndShowGiftEligibilityToast
    return fullCartMiniData
  } catch {
    // Ignore toast errors - không làm gián đoạn flow chính
    return undefined
  }
}

/**
 * Kiểm tra item vừa thêm có thuộc excluded collection không
 * So sánh quantity trước và sau khi add để xác định item mới
 */
function checkIfAddedItemIsExcluded({
  prevItems,
  items,
  excludeCollectionIds,
}: {
  prevItems: CartMiniItem[]
  items: CartMiniItem[]
  excludeCollectionIds: string[]
}): boolean {
  // Tạo map để track quantity trước khi add
  const prevMap = new Map<string, number>()
  for (const it of prevItems) {
    const key = `${it.id}-${it.variants.id}`
    prevMap.set(key, (prevMap.get(key) ?? 0) + (it.quantity ?? 0))
  }

  // Tìm item vừa thêm (quantity tăng hoặc item mới)
  for (const it of items) {
    const key = `${it.id}-${it.variants.id}`
    const prevQty = prevMap.get(key) ?? 0
    const nextQty = it.quantity ?? 0

    if (nextQty > prevQty || !prevMap.has(key)) {
      // Item này vừa được thêm - kiểm tra có thuộc excluded collection không
      const isExcluded = it.collections?.some((c) =>
        excludeCollectionIds.includes(c)
      )
      return !!isExcluded
    }
  }

  return false
}

/**
 * Tìm item vừa được thêm vào giỏ hàng
 * Đơn giản: Tìm item có index_id không có trong prevItems (item mới)
 */
function findAddedItem({
  prevItems,
  items,
}: {
  prevItems: CartMiniItem[]
  items: CartMiniItem[]
}): CartMiniItem | undefined {
  // Tạo Set các index_id từ prevItems để check nhanh
  const prevIndexIds = new Set(
    prevItems.map((it) => it.index_id).filter(Boolean)
  )

  // Tìm item đầu tiên có index_id không có trong prevItems (item mới)
  return items.find((it) => it.index_id && !prevIndexIds.has(it.index_id))
}

/**
 * Kiểm tra và hiển thị gift eligibility toast
 * Chỉ hiển thị khi:
 * - max_price_gift !== null
 * - max_price_gift là number (typeof max_price_gift === 'number')
 *
 * Note: Không thể dùng hook trong helper function này, nên chỉ check max_price_gift
 * Campaign check được thực hiện ở các component/hook khác (gift-exchange-button, use-exchange-gift, etc.)
 */
export async function checkAndShowGiftEligibilityToast({
  queryClient,
  imageUrl,
  prevItems,
  cartMiniData,
}: {
  queryClient: QueryClient
  imageUrl?: string
  prevItems?: CartMiniItem[]
  cartMiniData?: Awaited<ReturnType<typeof __cCartService.getCartMini>>
}) {
  try {
    // Lấy user data từ cache để check isLoggedIn (nếu cần fallback get-cart)
    // USER query được hydrate từ server nên luôn có trong cache
    // const userData = queryClient.getQueryData([QUERY_KEYS.USER])
    // const isLoggedIn = !!userData

    let maxPriceGift: number | null = null
    let mainProductIndexId: string | undefined = undefined

    // Nếu có cartMiniData từ params (reuse từ checkAndShowCampaignReminder), dùng luôn
    // Nếu không có, mới fetch (fallback cho các trường hợp khác)
    try {
      const cartMiniDataToUse =
        cartMiniData ||
        (await queryClient.fetchQuery({
          queryKey: [QUERY_KEYS.CART_MINI],
          queryFn: () => __cCartService.getCartMini(),
          staleTime: 0, // Force fetch fresh data, không dùng cache
        }))

      // Check xem cart-mini có max_price_gift không (có thể là optional field)
      const cartMiniMaxPriceGift = (
        cartMiniDataToUse as { max_price_gift?: number | null }
      )?.max_price_gift

      if (
        cartMiniMaxPriceGift !== null &&
        typeof cartMiniMaxPriceGift === 'number'
      ) {
        maxPriceGift = cartMiniMaxPriceGift

        // Tìm item vừa được add vào giỏ hàng
        // Nếu có prevItems, so sánh để tìm item vừa add
        // Nếu không có prevItems, fallback về logic cũ (tìm item đầu tiên có giá >= max_price_gift)
        if (prevItems && cartMiniDataToUse?.items) {
          const addedItem = findAddedItem({
            prevItems,
            items: cartMiniDataToUse.items,
          })

          // Chỉ lấy index_id nếu item vừa add có giá >= max_price_gift
          if (
            addedItem?.index_id
            // &&
            // addedItem.variants?.regular_price >= cartMiniMaxPriceGift
          ) {
            mainProductIndexId = addedItem.index_id
          }
        } else {
          // Fallback: Tìm main product từ cart-mini items (logic cũ)
          // Cart-mini có thể không có đầy đủ thông tin như cart (index_parent_id, allowed_to_choose_gifts)
          // Nên chỉ check giá và index_id
          const mainProduct = cartMiniDataToUse?.items?.find((item) => {
            if (!item.index_id) return false
            // Check giá sản phẩm >= max_price_gift
            return item.variants?.regular_price >= cartMiniMaxPriceGift
          })

          if (mainProduct?.index_id) {
            mainProductIndexId = mainProduct.index_id
          }
        }
      }
    } catch (cartMiniError) {
      // Nếu cart-mini có vấn đề, log warning nhưng không fallback
      // Tin tưởng cart-mini đúng rồi, không cần fallback về get-cart
      console.warn('Failed to fetch cart-mini:', cartMiniError)
    }

    // Fallback logic đã được bỏ - tin tưởng cart-mini đúng rồi
    // Nếu cần fallback về get-cart trong tương lai, uncomment đoạn code dưới:
    /*
    if (maxPriceGift === null || !mainProductIndexId) {
      const cartData = await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.CART, isLoggedIn],
        queryFn: () => __cCartService.getCart({ isLoggedIn }),
        staleTime: 0, // Force fetch fresh data, không dùng cache
      })

      const cartMaxPriceGift = cartData?.max_price_gift
      if (cartMaxPriceGift !== null && typeof cartMaxPriceGift === 'number') {
        maxPriceGift = cartMaxPriceGift

        // Tìm main product đủ điều kiện nhận quà từ cart
        // Main product phải:
        // - regular_price >= max_price_gift
        // - allowed_to_choose_gifts === true (nếu có)
        // - Không phải là gift (không có index_parent_id)
        const mainProduct = cartData?.items?.find((item) => {
          // Không phải là gift (không có index_parent_id)
          if (item.index_parent_id) return false

          // Check giá sản phẩm >= max_price_gift
          const isPriceEligible = item.regular_price >= cartMaxPriceGift

          // Check allowed_to_choose_gifts nếu có
          const isAllowedToChooseGifts =
            item.allowed_to_choose_gifts === true ||
            item.allowed_to_choose_gifts === undefined

          return isPriceEligible && isAllowedToChooseGifts
        })

        if (mainProduct?.index_id) {
          mainProductIndexId = mainProduct.index_id
        }
      }
    }
    */

    // Chỉ hiển thị toast nếu campaign đang active
    // max_price_gift phải khác null và là number
    if (maxPriceGift !== null && typeof maxPriceGift === 'number') {
      // Delay để hiển thị sau toast add to cart success
      setTimeout(() => {
        toast.giftEligibility({
          imageUrl,
          mainProductIndexId,
        })
      }, 300)
    }
  } catch {
    // Ignore toast errors - không làm gián đoạn flow chính
  }
}

/**
 * Kiểm tra và hiển thị buy-1-get-1-value-match toast
 * Chỉ hiển thị khi:
 * - Campaign đang active (checked ở calling code)
 * - max_eligible_price_for_gift !== undefined
 * - max_eligible_price_for_gift > 0
 *
 * Note: Campaign check được thực hiện ở useAddToCart hook
 */
export async function checkAndShowBuy1Get1ValueMatchToast({
  queryClient,
  cartMiniData,
}: {
  queryClient: QueryClient
  cartMiniData?: Awaited<ReturnType<typeof __cCartService.getCartMini>>
}) {
  try {
    // Nếu có cartMiniData từ params (reuse từ checkAndShowCampaignReminder), dùng luôn
    // Nếu không có, mới fetch (fallback cho các trường hợp khác)
    const cartMiniDataToUse =
      cartMiniData ||
      (await queryClient.fetchQuery({
        queryKey: [QUERY_KEYS.CART_MINI],
        queryFn: () => __cCartService.getCartMini(),
        staleTime: 0, // Force fetch fresh data, không dùng cache
      }))

    // Check xem cart-mini có max_eligible_price_for_gift không
    const maxEligiblePriceForGift = (
      cartMiniDataToUse as { max_eligible_price_for_gift?: number }
    )?.max_eligible_price_for_gift

    // Chỉ hiển thị toast nếu max_eligible_price_for_gift tồn tại và > 0
    if (
      maxEligiblePriceForGift !== undefined &&
      typeof maxEligiblePriceForGift === 'number' &&
      maxEligiblePriceForGift > 0
    ) {
      // Delay để hiển thị sau toast add to cart success
      setTimeout(() => {
        toast.buy1Get1ValueMatch({
          maxEligiblePrice: maxEligiblePriceForGift,
        })
      }, 300)
    }
  } catch {
    // Ignore toast errors - không làm gián đoạn flow chính
  }
}
